import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";

import { apiGet, apiPost, apiPut, apiDelete } from "helpers/apiService";
import SafeField from "helpers/SafeField";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";

const HelpCategory = () => {
  const [list, setList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchList();
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  /* ================= FETCH ================= */

  const fetchList = () => {
    apiGet("HelpCategory/allforagent?agentId=0&clientId=0")
      .then(res => setList(res.data))
      .catch(() => toast.error("Failed to load help categories"));
  };

  /* ================= CREATE ================= */

  const handleCreate = (values, { setSubmitting, resetForm }) => {
    toast.loading("Saving...");

    const payload = {
      helpCategoryTitle: values.helpCategoryTitle,
      agentId: parseInt(localStorage.getItem("userId")) || 0,
      isActive: true,
    };

    apiPost("HelpCategory", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Saved successfully");
        fetchList();
        resetForm();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Save failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= UPDATE ================= */

  const handleUpdate = (values, { setSubmitting }) => {
    toast.loading("Updating...");

    const payload = {
      id: values.id,
      helpCategoryId: values.helpCategoryId, // GUID from backend
      helpCategoryTitle: values.helpCategoryTitle,
      agentId: values.agentId,
      isActive: values.isActive,
    };

    apiPut("HelpCategory", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Updated successfully");
        fetchList();
        toggle();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Update failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= DELETE ================= */

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(result => {
      if (!result.isConfirmed) return;

      apiDelete(`HelpCategory/${id}`)
        .then(() => {
          toast.success("Deleted successfully");
          fetchList();
        })
        .catch(() => toast.error("Delete failed"));
    });
  };

  const openEdit = item => {
    setSelected(item);
    toggle();
  };

  /* ================= VALIDATION ================= */

  const validationSchema = Yup.object({
    helpCategoryTitle: Yup.string().required("Category title is required"),
  });

  return (
    <Container fluid className="page-content">
      <ToastContainer autoClose={2000} />
        <Row className="mb20">
          <Col xl="12">
            <Breadcrumbs
              title="General Settings"
              breadcrumbItem="Help Category"
            />
          </Col>
        </Row>

      <Row>
        <Col xl="12">
          {/* ===== ADD FORM ===== */}
          <Card>
            <CardBody>
             

              <Formik
                initialValues={{ helpCategoryTitle: "" }}
                validationSchema={validationSchema}
                onSubmit={handleCreate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label>Category Title *</Label>
                          <SafeField
                            name="helpCategoryTitle"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.helpCategoryTitle && touched.helpCategoryTitle
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name="helpCategoryTitle"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="2" className="d-flex align-items-end">
                        <Button type="submit" color="primary">
                          Save
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </CardBody>
          </Card>

          {/* ===== LIST ===== */}
          <Card>
            <CardBody>
              <h5>Help Category List</h5>

              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item, i) => (
                      <tr key={item.helpCategoryId || i}>
                        <td>{i + 1}</td>
                        <td>{item.helpCategoryTitle}</td>
                        <td>{item.isActive ? "Active" : "Inactive"}</td>
                        <td>
                            <ActionEdit onClick={() => openEdit(item)} />{" "}
                            <ActionDelete onClick={() => handleDelete(item.helpCategoryId)} />
                         
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ===== EDIT OFFCANVAS ===== */}
      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
        <OffcanvasHeader toggle={toggle}>Update Help Category</OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={selected}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
          >
            {({ handleChange }) => (
              <Form>
                <FormGroup>
                  <Label>Category Title *</Label>
                  <SafeField
                    name="helpCategoryTitle"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup check className="mb-3">
                  <SafeField type="checkbox" name="isActive" />
                  <Label check className="ms-2">Active</Label>
                </FormGroup>

                <Button type="submit" color="primary">
                  Update
                </Button>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
};

export default HelpCategory;
